import { useEffect, useState } from 'react';
import { Phone, Mail, User, MapPin, Calendar } from 'lucide-react';
import emailjs from 'emailjs-com';

const RECAPTCHA_SITE_KEY = '6LfHUj8rAAAAANSxaane7wlxlcpnA_Mw_QAjpUCx';

const ContactForm = () => {
  // list of block emails that spam me 
  const blockedEmails = [
    'july5331@myleadshero.com',
    'ronald.38estimationhub@gmail.com',
    'business@interiumcabinets.com',
    'jasonsamithseomarketing@gmail.com',
    'eliza@spynnpr.online',
  ];

  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    service: '',
    startTime: '',
    financing: '',
    message: '',
    consent: false,
  });

  const states = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'
  ];

  const services = [
    'COMPLETE REMODELING','KITCHEN REMODELING','BATHROOM REMODEL','ADU / GARAGE CONVERSION','EARTHQUAKE RETROFIT','FOUNDATIONS','EXTERIOR PAINTING','SOLAR PANELS','ENERGY-EFFICIENT WINDOWS','PAVERS','OUTDOOR LIVING','ARTIFICIAL TURF','HVAC SERVICES','ASPHALT ROOF','SHINGLES ROOF','TILE ROOF','FLAT ROOF','METAL ROOF','TPO ROOF','GUTTER'
  ];

  // Wait for global reCAPTCHA script from _app.js
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.execute) {
        setRecaptchaReady(true);
        return true;
      }
      return false;
    };

    if (checkRecaptcha()) return;

    const intervalId = setInterval(() => {
      if (checkRecaptcha()) {
        clearInterval(intervalId);
      }
    }, 500);

    return () => clearInterval(intervalId);
  }, []);

  const validateForm = () => {
    let valid = true;
    let errorsObj = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;

    // Email validation
    if (!emailRegex.test(formData.email)) {
      valid = false;
      errorsObj.email = 'Please enter a valid email address';
    }

    // Phone validation (10 digits, no spaces or dashes)
    if (!phoneRegex.test(formData.phone)) {
      valid = false;
      errorsObj.phone = 'Please enter a 10 digit phone number without spaces or dashes';
    }

    setErrors(errorsObj);
    return valid;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Only numbers for phone, max 10
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, phone: digitsOnly }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const {
      firstName,
      lastName,
      email,
      phone,
      city,
      state,
      service,
      startTime,
      financing,
      message,
    } = formData;

    if (!firstName || !lastName || !email || !phone || !state || !service || !financing || !startTime) {
      alert('Please fill all required fields.');
      return;
    }

    if (!recaptchaReady || !window.grecaptcha) {
      alert('reCAPTCHA is not ready yet.');
      return;
    }

    // Execute reCAPTCHA v3
    const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });
    const recaptchaResponse = await fetch('/api/verify-recaptcha', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    const recaptchaResult = await recaptchaResponse.json();
    console.log('ContactForm reCAPTCHA result', recaptchaResponse.status, recaptchaResult);

    if (!recaptchaResponse.ok) {
      alert('reCAPTCHA verification failed. Please try again.');
      return;
    }

    const submissionData = {
      ...formData,
      name: `${firstName} ${lastName}`,
      firstName: firstName.toLowerCase(),
      lastName: lastName.toLowerCase(),
      email: email.toLowerCase(),
      phone,
      city: city.toLowerCase(),
      state: state.toLowerCase(),
      service: service.toLowerCase(),
      financing: financing.toLowerCase(),
      startTime: startTime.toLowerCase(),
      message: message.toLowerCase(),
      'g-recaptcha-response': token,
    };

    // Block specific spammer email
    if (blockedEmails.includes(email.toLowerCase())) {
      setSuccessMessage('Your inquiry has been sent successfully! We will get back to you shortly.');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        city: '',
        state: '',
        service: '',
        startTime: '',
        financing: '',
        message: '',
        consent: false,
      });
      return;
    }

    // EmailJS Send Function
    emailjs
      .send(
        'service_modernBuild',   // Service ID
        'template_z9lbcbo',     // Template ID
        submissionData,
        '5lbOJijq8X39pr80P'     // User ID / public key
      )
      .then(() => {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
          window.gtag('event', 'form_submission', {
            event_category: 'lead',
            event_label: formData.service || 'unknown',
            value: 1,
          });
        }

        setSuccessMessage('Your inquiry has been sent successfully! We will get back to you shortly.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          city: '',
          state: '',
          service: '',
          startTime: '',
          financing: '',
          message: '',
          consent: false,
        });
      })
      .catch(() => {
        alert('Failed to send the message, please try again.');
      });
  };

  return (
    <div className="bg-background p-8">
      {successMessage && (
        <div className="mb-4 text-green-600 text-lg">
          {successMessage}
        </div>
      )}

      {/* Title and Subtitle */}
      <div className="text-left mb-4">
        <h2 className="text-md uppercase font-light tracking-[6px] text-gray mb-2">
          Get in Touch with Us
        </h2>
        <p className="text-2xl text-black font-gilda mb-4">Free Consultation Form</p>
        <span className="block h-[1px] bg-opacity-10 w-full bg-gray"></span>
      </div>

      <form id="contact-form" autoComplete="on" onSubmit={handleSubmit} className="bg-background space-y-4">
        <div className="flex flex-wrap md:flex-nowrap gap-4">
          {/* First Name and Last Name */}
          <div className="w-full md:w-1/2">
            <input
              type="text"
              name="firstName"
              autoComplete="given-name"
              value={formData.firstName}
              onChange={handleChange}
              required
              placeholder="* First Name"
              className="contactInput w-full p-5 placeholder-black"
            />
          </div>
          <div className="w-full md:w-1/2">
            <input
              type="text"
              name="lastName"
              autoComplete="family-name"
              value={formData.lastName}
              onChange={handleChange}
              required
              placeholder="* Last Name"
              className="contactInput w-full p-5 placeholder-black"
            />
          </div>
        </div>

        <div className="flex flex-wrap md:flex-nowrap gap-4">
          {/* Email and Phone */}
          <div className="w-full md:w-1/2">
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="* Email"
              className="contactInput w-full p-5 placeholder-black"
            />
            {errors.email && (
              <p className="text-xs text-red-600 mt-1">{errors.email}</p>
            )}
          </div>
          <div className="w-full md:w-1/2">
            <input
              type="text"
              name="phone"
              autoComplete="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              pattern="\d{10}"
              maxLength={10}
              inputMode="numeric"
              placeholder="* Phone (10 digits, no spaces or dashes)"
              className="contactInput w-full p-5 placeholder-black"
            />
            {errors.phone && (
              <p className="text-xs text-red-600 mt-1">{errors.phone}</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap md:flex-nowrap gap-4">
          {/* City and State */}
          <div className="w-full md:w-1/2">
            <input
              type="text"
              name="city"
              autoComplete="address-level2"
              value={formData.city}
              onChange={handleChange}
              required
              placeholder="* City"
              className="contactInput w-full p-5 placeholder-black"
            />
          </div>
          <div className="w-full md:w-1/2">
            <label htmlFor="state" className="sr-only">
              Select State
            </label>
            <select
              id="state"
              name="state"
              autoComplete="address-level1"
              value={formData.state}
              onChange={handleChange}
              required
              className="custom-select w-full p-5 placeholder-black"
            >
              <option value="" disabled>
                * Select State
              </option>
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap md:flex-nowrap gap-4">
          {/* Service and Financing */}
          <div className="w-full md:w-1/2">
            <label htmlFor="service" className="sr-only">
              Select Service
            </label>
            <select
              id="service"
              name="service"
              autoComplete="off"
              value={formData.service}
              onChange={handleChange}
              required
              className="custom-select w-full p-5 placeholder-black"
            >
              <option value="" disabled>
                * Select Service
              </option>
              {services.map((service) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full md:w-1/2">
            <label htmlFor="financing" className="sr-only">
              Select Financing
            </label>
            <select
              id="financing"
              name="financing"
              autoComplete="off"
              value={formData.financing}
              onChange={handleChange}
              required
              className="custom-select w-full p-5 placeholder-black"
            >
              <option value="" disabled>
                * Interested in finance?
              </option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>

        {/* Project Start Time */}
        <div>
          <label htmlFor="startTime" className="sr-only">
            Project start time
          </label>
          <select
            id="startTime"
            name="startTime"
            autoComplete="off"
            value={formData.startTime}
            onChange={handleChange}
            required
            className="custom-select w-full p-5 placeholder-black"
          >
            <option value="" disabled>
              * When do you want to start the project?
            </option>
            <option value="now">Now</option>
            <option value="3months">In 3 Months</option>
            <option value="6months">In 6 Months</option>
            <option value="dontknow">Do not know yet</option>
          </select>
        </div>

        {/* Message (Optional) */}
        <div>
          <textarea
            name="message"
            autoComplete="off"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your project (Optional)"
            className="contactInput w-full p-5 placeholder-black"
          />
        </div>

        {/* Checkbox */}
        <div className="flex items-center">
          <input
            id="consent"
            type="checkbox"
            name="consent"
            value="yes"
            autoComplete="off"
            checked={formData.consent}
            onChange={handleChange}
            required
            className="custom-checkbox"
          />
          <label htmlFor="consent" className="ml-2 text-sm text-black">
            I consent to being contacted by Modern Build or its contractors, and to be added to the mailing list.
          </label>
        </div>

        {/* Submit Button */}
        <button
          aria-label="submit form"
          disabled={!recaptchaReady}
          type="submit"
          className="w-full p-4 bg-primary uppercase text-white text-xl hover:bg-black hover:text-white transition duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Submit Inquiry
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
